import type { FindingView } from "../../api/types.js";

export interface ExplainedSourceLine {
  number: number;
  content: string;
  cited: boolean;
  explanation: string;
}

const CONTEXT_LINE_LIMIT = 5;

function storedLines(value: string): string[] {
  if (!value) return [];
  const lines = value.split("\n");
  if (value.endsWith("\n")) lines.pop();
  return lines;
}

function syntaxExplanation(content: string, cited: boolean): string {
  const trimmed = content.trim();
  const citation = cited ? " Detector-cited line." : "";
  if (!trimmed) return `Blank separator; no behavior is visible on this line.${citation}`;
  if (/^(?:\/\/|#(?!\[)|\/\*|\*|<!--|--)/u.test(trimmed)) {
    return `Comment text; it can describe intent but does not prove runtime behavior.${citation}`;
  }
  if (/^(?:import\b|from\s+\S+\s+import\b|require\s*\(|using\b|include\b)/u.test(trimmed)) {
    return `Imports or references another module; that module's behavior is not visible here.${citation}`;
  }
  if (/^(?:if|else\s+if|while|for|switch|case|when)\b/u.test(trimmed)) {
    return `Control-flow syntax evaluates or selects a branch; the full outcome depends on values outside this line.${citation}`;
  }
  if (/^(?:return|yield|throw|raise)\b/u.test(trimmed)) {
    return `Transfers control with the shown expression; its caller or handler is not visible here.${citation}`;
  }
  if (/^(?:const|let|var|final|val)\b/u.test(trimmed)) {
    return `Declares a binding and may initialize it with the shown expression; later uses are not visible here.${citation}`;
  }
  if (
    /^(?:export\s+)?(?:async\s+)?function\b|^(?:public|private|protected|static|async|def|func|fun)\b/u.test(
      trimmed,
    )
  ) {
    return `Declares a callable or member boundary; behavior requires the surrounding body.${citation}`;
  }
  if (/^[}\])]+[;,]?$/u.test(trimmed)) {
    return `Closes a nearby block or expression; the opening construct is outside this line.${citation}`;
  }
  if (/^[A-Za-z_$][\w$.-]*\s*[:=]/u.test(trimmed)) {
    return `Assigns or configures the named value using the shown expression; its downstream effect is unknown here.${citation}`;
  }
  if (/(?:[A-Za-z_$][\w$]*\.)?[A-Za-z_$][\w$]*\s*\(/u.test(trimmed)) {
    return `Contains a call expression; the callee's implementation and runtime inputs are not visible here.${citation}`;
  }
  if (/[{[(]\s*$/u.test(trimmed)) {
    return `Opens a block, collection, or expression continued by nearby lines; its exact role is not certain from this line alone.${citation}`;
  }
  return `The syntax role cannot be determined confidently from this line alone.${citation}`;
}

/** Five stored lines centered on the cited range, for hop hover and briefing. */
export function hopExhibitLines(finding: FindingView, count = 5): ExplainedSourceLine[] {
  const lines = explainedSourceLines(finding);
  if (lines.length <= count) return lines;
  const cited = lines.findIndex((line) => line.cited);
  const center = cited >= 0 ? cited : Math.floor(lines.length / 2);
  const start = Math.max(0, Math.min(center - Math.floor(count / 2), lines.length - count));
  return lines.slice(start, start + count);
}

export function findingSourceHref(finding: FindingView): string {
  const returnHash = `#source-${finding.id}-L${finding.location.lineStart}`;
  const returnTo =
    typeof window === "undefined"
      ? `/findings${returnHash}`
      : `${window.location.pathname}${window.location.search}${returnHash}`;
  if (finding.sourcePermalink) return finding.sourcePermalink;
  return `/api/v1/findings/${encodeURIComponent(finding.id)}/source?returnTo=${encodeURIComponent(returnTo)}#L${finding.location.lineStart}`;
}

export function snapshotFileHref(scanId: string, path: string, line: number): string {
  const returnTo =
    typeof window === "undefined"
      ? "/findings"
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const params = new URLSearchParams({
    path,
    lineStart: String(Math.max(1, line)),
    lineEnd: String(Math.max(1, line)),
    returnTo,
  });
  return `/api/v1/scans/${encodeURIComponent(scanId)}/source?${params.toString()}#L${Math.max(1, line)}`;
}

export function ruleQueueHref(ruleId: string, scanId?: string): string {
  const params = new URLSearchParams({ preset: "all", ruleIds: ruleId });
  if (scanId) params.set("scanId", scanId);
  return `/findings?${params.toString()}`;
}

export function cweCatalogHref(cwe: string): string | null {
  const match = /^CWE-(\d+)$/iu.exec(cwe.trim());
  if (!match) return null;
  return `https://cwe.mitre.org/data/definitions/${match[1]}.html`;
}

export function explainedSourceLines(finding: FindingView): ExplainedSourceLine[] {
  const before = storedLines(finding.evidence.contextBefore).slice(-CONTEXT_LINE_LIMIT);
  const expectedCitedLines = Math.max(1, finding.location.lineEnd - finding.location.lineStart + 1);
  const storedCited = storedLines(finding.evidence.redactedSnippet);
  const cited = Array.from({ length: expectedCitedLines }, (_, index) =>
    index < storedCited.length
      ? (storedCited[index] as string)
      : "[Source line unavailable in stored redacted evidence]",
  );
  const after = storedLines(finding.evidence.contextAfter).slice(0, CONTEXT_LINE_LIMIT);
  const firstLine = Math.max(1, finding.location.lineStart - before.length);
  return [...before, ...cited, ...after].map((content, index) => {
    const isCited = index >= before.length && index < before.length + cited.length;
    return {
      number: firstLine + index,
      content,
      cited: isCited,
      explanation: syntaxExplanation(content, isCited),
    };
  });
}
